import React, { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { matchesTemplatePickerSearch } from "../../utils/orderedTokenSearch";

function SearchableDropdown({
  label,
  value,
  options,
  onChange,
  placeholder,
  emptyText = "No options available.",
  emptySearchText = "No matches found.",
  dir = "ltr",
  showFavoriteControls = false,
  onFavoriteToggle,
  favoriteLabel = "Add to favorites",
  unfavoriteLabel = "Remove from favorites",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selectedOption = options.find((option) => option.value === value);
  const selectedLabel = selectedOption?.label;
  const trimmedQuery = query.trim();
  const deferredQuery = useDeferredValue(trimmedQuery);
  const filteredOptions = useMemo(() => {
    if (!deferredQuery) return options;
    return (options || []).filter((option) =>
      matchesTemplatePickerSearch(option, deferredQuery),
    );
  }, [options, deferredQuery]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isDisabled = options.length === 0;
  const canFavorite =
    showFavoriteControls && typeof onFavoriteToggle === "function";

  return (
    <div className="relative" ref={containerRef} dir={dir}>
      <button
        type="button"
        className={`w-full border border-gray-300 dark:border-gray-600 p-3 rounded-full bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-400 dark:focus:ring-green-600 cursor-pointer flex items-center justify-between ${
          isDisabled ? "opacity-60 cursor-not-allowed" : ""
        }`}
        onClick={() => {
          if (!isDisabled) {
            setIsOpen((prev) => !prev);
          }
        }}
        disabled={isDisabled}
        aria-expanded={isOpen}
      >
        <span className="truncate">
          {selectedLabel || label || placeholder || ""}
        </span>
        <svg
          className={`w-5 h-5 text-gray-900 dark:text-gray-100 ${dir === "rtl" ? "mr-2" : "ml-2"}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 9l-7 7-7-7"
          ></path>
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-[100] mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-xl shadow-lg p-2 border border-gray-300 dark:border-gray-600">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder || label || ""}
            className="w-full mb-2 px-2 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-green dark:focus:ring-green-dark"
            dir={dir}
          />
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400 px-2 py-1">
                {options.length === 0 ? emptyText : emptySearchText}
              </p>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={`flex items-center gap-1 rounded-lg ${
                    option.value === value
                      ? "bg-gray-300 dark:bg-gray-600"
                      : "hover:bg-gray-300 dark:hover:bg-gray-600"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => {
                      onChange(option.value);
                      setIsOpen(false);
                      setQuery("");
                    }}
                    className={`min-w-0 flex-1 ${dir === "rtl" ? "text-right" : "text-left"} px-3 py-2 text-sm text-gray-900 dark:text-gray-100`}
                    dir="auto"
                  >
                    <div className="flex flex-col items-start gap-0.5">
                      <span className="text-sm leading-4">{option.label}</span>
                      {option.subLabel ? (
                        <span className="text-[11px] leading-3 text-gray-600 dark:text-gray-300">
                          {option.subLabel}
                        </span>
                      ) : null}
                    </div>
                  </button>
                  {canFavorite && (
                    <button
                      type="button"
                      className="flex-shrink-0 p-2 rounded-full touch-manipulation"
                      aria-label={
                        option.isFavorite ? unfavoriteLabel : favoriteLabel
                      }
                      title={
                        option.isFavorite ? unfavoriteLabel : favoriteLabel
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        onFavoriteToggle(option.value, !option.isFavorite);
                      }}
                    >
                      <svg
                        className={`w-4 h-4 ${
                          option.isFavorite
                            ? "text-amber-400"
                            : "text-gray-500 dark:text-gray-400"
                        }`}
                        viewBox="0 0 24 24"
                        fill={option.isFavorite ? "currentColor" : "none"}
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchableDropdown;
